import { useCallback, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import { todayKST } from '../../../utils/date';

const DEFAULT_COMPANY_CODE = 'PINE';

function normalizeWeightInput(value) {
  const weight = Number(value);
  if (!Number.isFinite(weight) || weight < 0) return null;
  return weight;
}

export function useTicketWeightHistory({ onSaved } = {}) {
  const { showToast } = useToast();
  const [activeWeightTicket, setActiveWeightTicket] = useState(null);
  const [weightHistoryRows, setWeightHistoryRows] = useState([]);
  const [weightHistoryLoading, setWeightHistoryLoading] = useState(false);
  const [weightHistorySaving, setWeightHistorySaving] = useState(false);
  const [weightHistoryForm, setWeightHistoryForm] = useState({
    effectiveFrom: todayKST(),
    weight: '',
    memo: '',
  });

  const fetchWeightHistory = useCallback(async (ticketId) => {
    if (!ticketId) return;
    setWeightHistoryLoading(true);
    const r = await apiClient.get(`/api/admin/ticket-types/${ticketId}/weight-history?company_code=${DEFAULT_COMPANY_CODE}`);
    setWeightHistoryLoading(false);
    if (!r.ok) {
      showToast(r.data?.message || '환산장수 이력을 불러오지 못했습니다.', 'error');
      return;
    }
    setWeightHistoryRows(Array.isArray(r.data?.data) ? r.data.data : []);
  }, [showToast]);

  const openWeightHistory = useCallback(async (ticket) => {
    setActiveWeightTicket(ticket);
    setWeightHistoryForm({
      effectiveFrom: todayKST(),
      weight: String(ticket?.current_weight ?? 1),
      memo: '',
    });
    setWeightHistoryRows([]);
    await fetchWeightHistory(ticket?.id);
  }, [fetchWeightHistory]);

  const closeWeightHistory = useCallback(() => {
    setActiveWeightTicket(null);
    setWeightHistoryRows([]);
    setWeightHistoryForm({
      effectiveFrom: todayKST(),
      weight: '',
      memo: '',
    });
  }, []);

  const updateWeightHistoryForm = useCallback((field, value) => {
    setWeightHistoryForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const saveWeightHistory = useCallback(async () => {
    if (!activeWeightTicket?.id) return;
    const effectiveFrom = String(weightHistoryForm.effectiveFrom || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom)) {
      showToast('적용 시작일을 선택해 주세요.', 'error');
      return;
    }
    const weight = normalizeWeightInput(weightHistoryForm.weight);
    if (weight === null) {
      showToast('환산장수는 0 이상 숫자로 입력해 주세요.', 'error');
      return;
    }

    setWeightHistorySaving(true);
    const r = await apiClient.put(`/api/admin/ticket-types/${activeWeightTicket.id}/weight`, {
      company_code: DEFAULT_COMPANY_CODE,
      effective_from: effectiveFrom,
      weight,
      memo: weightHistoryForm.memo,
    });
    setWeightHistorySaving(false);
    if (!r.ok) {
      showToast(r.data?.message || '환산장수 저장 실패', 'error');
      return;
    }

    showToast('환산장수 이력이 저장되었습니다.', 'success');
    setWeightHistoryForm((prev) => ({ ...prev, memo: '' }));
    await fetchWeightHistory(activeWeightTicket.id);
    if (typeof onSaved === 'function') await onSaved();
  }, [activeWeightTicket, fetchWeightHistory, onSaved, showToast, weightHistoryForm]);

  return {
    activeWeightTicket,
    weightHistoryRows,
    weightHistoryLoading,
    weightHistorySaving,
    weightHistoryForm,
    openWeightHistory,
    closeWeightHistory,
    updateWeightHistoryForm,
    saveWeightHistory,
  };
}
